import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { makeRequest } from "@/helpers/apiWrapper";
import { formatCurrency } from "@/helpers/formatCurrency";
import ProductCardUpdated from "@/Components/Section/Products/ProductCardUpdated";
import ProductCardUpdatedSkeleton from "@/Components/Common/Loading/ProductCardUpdatedLoading";
import { Product } from "@/Store/cartStore";
import useWishlistStore from "@/Store/wishlistStore";
import { useUserStore } from "@/Store/UserInfoStore";

const ITEMS_PER_PAGE = 8;

const ProductWishList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { wishlist, toggleWishlist, isInWishlist, addToWishlist, clearWishlist } = useWishlistStore();
  const { user } = useUserStore()
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["wishlist"],
    queryFn: () =>
      makeRequest({
        endpoint: "/api/product/get-wishlist",
        method: "GET",
      }).then((res) => res.data),
  });

  const products: Product[] = data?.products || [];

  useEffect(() => {
    if (products.length) {
      clearWishlist();
      products.forEach(product => {
        addToWishlist(product);
      });
    }
  }, [products, addToWishlist, clearWishlist]);

  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return products.slice(start, end);
  }, [products, currentPage]);

  const { mutateAsync: toggleWishlistApi } = useMutation({
    mutationFn: (productId: string) =>
      makeRequest({
        endpoint: `/api/product/toggle-wishlist/${productId}`,
        method: "PUT",
      }),
    onMutate: async (productId: string) => {
      toggleWishlist({ _id: productId } as Product);

      return { productId };
    },

    onError: (_err, productId) => {
      toggleWishlist({ _id: productId } as Product);
    },
  });
  const handleToggleWishlist = (product: Product) => {
    if (!user) {
      navigate("/login")
      return
    }
    toggleWishlistApi(product._id);
  };
  return (
    <div className="min-h-screen bg-gray-50 px-4">
      <div className="p-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-1 text-left">
          {t("My Wishlist")}
        </h1>

        <p className="text-gray-600 mb-6">
          {products.length} {products.length === 1 ? t("product") : t("products")}

        </p>
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 10 }).map((_, idx) => (
              <ProductCardUpdatedSkeleton key={idx} />
            ))}
          </div>
        ) : paginatedProducts.length === 0 ? (
          <div className="text-center py-16">
            <h3 className="text-xl font-semibold">{t("Your wishlist is empty")}</h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {paginatedProducts.map((product) => (
              <ProductCardUpdated
                key={product._id}
                img={product?.imageUrls?.[0]?.url}
                title={product.name}
                price={formatCurrency(
                  product?.convertedAmount?.amount || product?.price,
                  product?.convertedAmount?.currency || product?.currency
                )}
                rating={product.rating || 0}
                sold={product.sold || 0}
                liked={isInWishlist(product._id)}
                onclick={() => navigate(`/product/${product._id}`)}
                onToggleWishlist={() => handleToggleWishlist(product)}
                product={product}

              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-8 gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
              {t("Previous")}
            </button>

            {Array.from({ length: totalPages }).map((_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 rounded ${page === currentPage
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200"
                    }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
              {t("Next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductWishList;
